import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import Navigation from "./Navigation";
import Footer from "./Footer";
import SEO from "./SEO";
import { OAKMERE_GALLERY, HIDE_GALLERY, DUTTON_HUT_GALLERY, ACTON_BRIDGE_GALLERY, LOCK_KEEPERS_GALLERY, OAKMERE_COTTAGE_GALLERY, PROPERTIES, SITE } from "../../data/content";

// SEO metadata per property gallery. Each pair contains a unique title and
// description with target long-tail keywords baked in.
const GALLERY_SEO = {
  "lock-keepers-cottage": {
    title: "Lock Keeper's Cottage Gallery — Victorian Cottage with Hot Tub, Dutton Locks",
    description: "Photo gallery of Lock Keeper's Cottage, a Victorian 2-bed holiday cottage with wood-fired hot tub on a private island on the River Weaver at Dutton Locks, Northwich.",
    path: "/lock-keepers-gallery",
  },
  "acton-bridge-cottage": {
    title: "Acton Bridge Cottage Gallery — Victorian Village Holiday Let, Cheshire",
    description: "Photo gallery of Acton Bridge Cottage, a 2-bed Victorian holiday cottage with meadow orchard in Acton Bridge village, Cheshire — near Delamere Forest and the River Weaver.",
    path: "/acton-bridge-gallery",
  },
  "oakmere-cottage": {
    title: "2 Bed Cottage at Oakmere — Lakeside Holiday Cottage, Delamere Forest",
    description: "Photo gallery of a beautifully refurbished 2-bedroom lakeside cottage at Oakmere, Cheshire. 5 minutes from Delamere Forest, off the Whitegate Way. Book direct.",
    path: "/oakmere-cottage-gallery",
  },
  "shepherds-hut-dutton": {
    title: "Shepherd's Hut at Dutton Locks — Hot Tub Retreat on the River Weaver",
    description: "Photo gallery of our hand-built Shepherd's Hut at Dutton Locks with wood-fired hot tub on a private river island. Fully refitted 2026. Romantic Cheshire escape.",
    path: "/dutton-locks-hut-gallery",
  },
  "oakmere": {
    title: "Oakmere Shepherd's Hut — Lakeside Hot Tub Retreat, Cheshire",
    description: "Photo gallery of our Oakmere shepherd's hut overlooking a private mere with wood-fired hot tub and elevated deck. Near Delamere Forest, Cheshire.",
    path: "/oakmere-gallery",
  },
  "the-hide": {
    title: "The Hide — Lakeside Lodge, New Cheshire Holiday Retreat 2025",
    description: "Photo gallery of The Hide, our new-for-2025 lakeside lodge with floor-to-ceiling glass in secluded Cheshire woodland. Sleeps 2.",
    path: "/the-hide-gallery",
  },
};

// Reusable gallery page. Pass `propertyId` to render the correct property
// metadata and image set.
export default function PropertyGallery({ propertyId }) {
  const [lightbox, setLightbox] = useState(null);

  const property = PROPERTIES.find((p) => p.id === propertyId);
  const seo = GALLERY_SEO[propertyId];
  const images =
    propertyId === "oakmere"
      ? OAKMERE_GALLERY
      : propertyId === "the-hide"
        ? HIDE_GALLERY
        : propertyId === "shepherds-hut-dutton"
          ? DUTTON_HUT_GALLERY
          : propertyId === "acton-bridge-cottage"
            ? ACTON_BRIDGE_GALLERY
            : propertyId === "lock-keepers-cottage"
              ? LOCK_KEEPERS_GALLERY
              : propertyId === "oakmere-cottage"
                ? OAKMERE_COTTAGE_GALLERY
                : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    // Title is managed by <SEO> / react-helmet-async — do not override here.
  }, [property]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!property) {
    return (
      <div className="app-shell">
        <Navigation />
        <main className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl" style={{ color: "var(--dl-ink)" }}>
            Gallery not found
          </h1>
          <Link to="/" className="btn-primary mt-10 inline-flex">Back home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell" data-testid={`gallery-page-${propertyId}`}>
      {seo && (
        <SEO title={seo.title} description={seo.description} path={seo.path} image={images[0]} />
      )}
      <Navigation />

      <main>
        <section className="pt-32 sm:pt-40 pb-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Link
              to="/"
              data-testid="gallery-back-link"
              className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase transition-colors"
              style={{ color: "var(--dl-ink-2)" }}
            >
              <ArrowLeft size={14} /> Back to Cheshire Holidays
            </Link>

            <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-2xl">
                <p className="section-eyebrow">
                  <span className="divider-rule" /> Photo Gallery
                </p>
                <h1
                  className="mt-5 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02]"
                  style={{ color: "var(--dl-ink)", fontWeight: 400 }}
                >
                  {property.name}
                  <br />
                  <span className="serif-italic">Gallery.</span>
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed" style={{ color: "var(--dl-ink-2)" }}>
                  {property.description}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {property.tourUrl && (
                  <a
                    href={property.tourUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="gallery-tour-link"
                    className="btn-secondary"
                  >
                    Walkthrough Tour
                  </a>
                )}
                <Link to="/#contact" data-testid="gallery-reserve-link" className="btn-secondary">
                  Enquire
                </Link>
                <a
                  data-testid="gallery-reserve-external"
                  href={SITE.bookingExternal}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                >
                  Reserve Now
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div
              className="grid gap-2 sm:gap-3"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
              data-testid="gallery-grid"
            >
              {images.map((entry, i) => {
                const src = typeof entry === "string" ? entry : entry.src;
                const rotate = typeof entry === "string" ? 0 : entry.rotate || 0;
                const rotated = rotate % 180 !== 0;
                return (
                  <button
                    key={src}
                    onClick={() => setLightbox({ src, rotate })}
                    data-testid={`gallery-thumb-${i}`}
                    className="relative media-zoom aspect-[4/3] w-full border bg-white overflow-hidden"
                    style={{ borderColor: "var(--dl-border)" }}
                    aria-label={`Open photo ${i + 1}`}
                  >
                    <img
                      src={src}
                      alt={`${property.name} — Cheshire holiday cottage photo ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={
                        rotate
                          ? {
                              transform: `rotate(${rotate}deg)`,
                              transformOrigin: "center center",
                              width: rotated ? "133%" : "100%",
                              height: rotated ? "75%" : "100%",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              translate: "-50% -50%",
                            }
                          : undefined
                      }
                    />
                  </button>
                );
              })}
            </div>

            <div
              className="mt-16 border-t pt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              style={{ borderColor: "var(--dl-border)" }}
            >
              <p className="text-sm" style={{ color: "var(--dl-ink-2)" }}>
                Ready to book? Call {SITE.phone} or send a quick enquiry.
              </p>
              <a
                data-testid="gallery-cta-bottom"
                href={SITE.bookingExternal}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
              >
                Reserve Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10"
          style={{ background: "rgba(27,59,54,0.92)" }}
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-6 right-6 w-11 h-11 border flex items-center justify-center"
            style={{ borderColor: "rgba(250,249,246,0.4)", color: "var(--dl-bg)" }}
          >
            <X size={18} />
          </button>
          <img
            src={lightbox.src}
            alt={property.name}
            className="max-h-full max-w-full object-contain"
            style={
              lightbox.rotate
                ? { transform: `rotate(${lightbox.rotate}deg)` }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
